import { useMemo, useState } from 'react';
import { Warehouse } from 'lucide-react';
import type { WarehouseReport, WarehouseReportRow } from '@eombor/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import { Badge, cn, type Column } from '@eombor/ui';
import type { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { ResponsiveTable } from '../../../shared/ui/ResponsiveTable.js';
import { Segmented } from '../../../shared/ui/Segmented.js';
import { ReportCard } from './ReportCard.js';

/**
 * OMBORLAR KESIMI — rahbarning «qayerda?» savoli.
 *
 * Yig'ma raqam («mintaqada 18 000 dona») hech qanday amalga olib
 * bormaydi. Kesim esa olib boradi: qaysi omborda qoldiq kam qolgan,
 * qaysi birida mahsulot olib ketilmay yotibdi, qaysi biriga uzoq
 * vaqtdan beri hech narsa kelmagan.
 *
 * Doira SERVERDA qo'llanadi: MTU rahbari butun mintaqaning satrlarini,
 * korxona rahbari faqat o'zinikini oladi — bu yerda alohida filtr yo'q
 * va bo'lmasligi ham kerak.
 */

type SortKey = 'reserved' | 'low' | 'qty' | 'name';

const format = (value: number): string => value.toLocaleString('ru-RU');

/** `2026-08-20T…` → `20.08.2026`. Noto'g'ri sana O'ZGARISHSIZ qaytadi. */
const dateText = (iso: string | null): string => {
  if (iso === null) return '—';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(at.getDate())}.${pad(at.getMonth() + 1)}.${at.getFullYear()}`;
};

/**
 * Bandlik — ishlatilgan katakning JAMIGA nisbati.
 *
 * Katagi umuman yo'q ombor (hali qurilmagan javonlar) uchun `null`:
 * nolga bo'lish o'rniga chiziqcha chiziladi, «0% band» esa yolg'on
 * bo'lardi — u yerda band qilinadigan narsaning o'zi yo'q.
 */
const fillOf = (row: WarehouseReportRow): number | null =>
  row.cellCount === 0 ? null : Math.round((row.usedCellCount / row.cellCount) * 100);

const SORTERS: Record<SortKey, (a: WarehouseReportRow, b: WarehouseReportRow) => number> = {
  reserved: (a, b) => b.reservedQty - a.reservedQty,
  low: (a, b) => b.lowCount - a.lowCount,
  qty: (a, b) => b.totalQty - a.totalQty,
  name: (a, b) => a.name.localeCompare(b.name),
};

/** Qatorning ichidagi ingichka ulush chizig'i — sonni o'qimasdan ko'rinadi. */
const Fill = ({ percent }: { percent: number | null }) => {
  if (percent === null) return <span className="text-text-faint">—</span>;
  return (
    <span className="flex items-center justify-end gap-2">
      <span aria-hidden className="h-1.5 w-20 overflow-hidden rounded-full bg-surface-2">
        <span
          className={cn(
            'block h-full rounded-full',
            /* 90% dan yuqori — javon to'lib qolgan, yangi tushum
               joylashtirishda muammo bo'ladi. */
            percent >= 90 ? 'bg-danger' : percent >= 70 ? 'bg-warn' : 'bg-success',
          )}
          style={{ width: `${String(Math.max(2, percent))}%` }}
        />
      </span>
      <span className="tabular w-9 text-right">{percent}%</span>
    </span>
  );
};

export interface WarehouseBlockProps {
  query: UseQueryResult<WarehouseReport, ApiClientError>;
}

export const WarehouseBlock = ({ query }: WarehouseBlockProps) => {
  const [sort, setSort] = useState<SortKey>('reserved');

  const rows = useMemo(() => [...(query.data?.rows ?? [])].sort(SORTERS[sort]), [query.data, sort]);

  const columns: Column<WarehouseReportRow>[] = [
    {
      key: 'name',
      header: t('report.whName'),
      sticky: 'left',
      render: (row) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-medium text-text">{row.name}</span>
          <span className="truncate text-xs text-text-faint">{row.orgUnitName}</span>
        </span>
      ),
    },
    /* Kodga ANIQ en beriladi: `OMB-TSH-1` avtomatik kenglikda uch
       qatorga sinib, qator balandligini ikki barobar oshirardi. */
    { key: 'code', header: t('report.whCode'), mono: true, width: '9rem' },
    {
      key: 'itemCount',
      header: t('report.whItems'),
      align: 'right',
      mono: true,
      width: '6rem',
      render: (row) => format(row.itemCount),
    },
    {
      key: 'totalQty',
      header: t('report.whQty'),
      align: 'right',
      mono: true,
      width: '8rem',
      render: (row) => format(row.totalQty),
    },
    {
      key: 'reservedQty',
      header: t('report.whReserved'),
      align: 'right',
      mono: true,
      width: '10rem',
      /* Nol band — bu YAXSHI holat va urg'u talab qilmaydi: doim
         bo'yalgan ustun o'qilmay qolardi. */
      render: (row) =>
        row.reservedQty === 0 ? (
          <span className="text-text-faint">0</span>
        ) : (
          <span className="font-medium text-warn">{format(row.reservedQty)}</span>
        ),
    },
    {
      key: 'fill',
      header: t('report.whFill'),
      align: 'right',
      width: '11rem',
      render: (row) => <Fill percent={fillOf(row)} />,
    },
    {
      key: 'lowCount',
      header: t('report.whLow'),
      align: 'right',
      width: '8rem',
      render: (row) =>
        row.lowCount === 0 ? (
          <span className="text-text-faint">0</span>
        ) : (
          <Badge tone="danger">{format(row.lowCount)}</Badge>
        ),
    },
    {
      key: 'lastReceiptAt',
      header: t('report.whLastIn'),
      align: 'right',
      mono: true,
      width: '9rem',
      render: (row) => dateText(row.lastReceiptAt),
    },
  ];

  const totals = query.data?.totals;

  return (
    <ReportCard
      icon={Warehouse}
      tone="info"
      title={t('report.whTitle')}
      hint={
        totals === undefined
          ? t('report.whHintEmpty')
          : t('report.whHint', { count: totals.warehouseCount, items: totals.itemCount })
      }
      pending={query.isPending}
      error={query.error ?? null}
      empty={rows.length === 0}
      emptyText={t('report.whEmpty')}
      actions={
        <Segmented
          label={t('report.whSort')}
          value={sort}
          onChange={setSort}
          options={[
            { value: 'reserved', label: t('report.whSortReserved') },
            { value: 'low', label: t('report.whSortLow') },
            { value: 'qty', label: t('report.whSortQty') },
            { value: 'name', label: t('report.whSortName') },
          ]}
        />
      }
    >
      <ResponsiveTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.warehouseId}
        density="compact"
        caption={t('report.whTitle')}
        card={{ title: 'name', code: 'code' }}
      />
    </ReportCard>
  );
};
